import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsAPI, offersAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { CandidateJourneyCompact, fmtDays } from '../components/CandidateJourney';

const STAGES = ['applied', 'screening', 'assessment', 'interview', 'bgv', 'credentials', 'offer', 'hired'];
const STAGE_LABELS = {
  applied: 'Applied', screening: 'Screening', assessment: 'Assessment',
  interview: 'Interview', bgv: 'BGV', credentials: 'Credentials', offer: 'Offer', hired: 'Hired',
};
const STAGE_COLORS = {
  applied: '#94A3B8', screening: '#0076A3', assessment: '#F2600C',
  interview: '#00A0E0', bgv: '#00A0E0', credentials: '#DB2777', offer: '#D97706', hired: '#059669',
};

export default function PipelinePage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await applicationsAPI.listAll();
      setApplications(res.data);
    } catch (err) { toast.error('Failed to load pipeline'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Use the REAL recruiter-controlled stage (fallback to applied)
  const getStage = (app) => {
    const s = app.pipeline_stage || 'applied';
    return STAGES.includes(s) ? s : 'applied';
  };

  // time spent in the current stage (from journey)
  const timeInStage = (app) => {
    const cur = (app.journey?.stages || []).find(s => s.status === 'current');
    return cur ? cur.duration_days : null;
  };

  const grouped = {};
  STAGES.forEach(s => grouped[s] = []);
  applications.forEach(a => {
    const stage = getStage(a);
    if (grouped[stage]) grouped[stage].push(a);
  });

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>Loading pipeline...</div>;

  return (
    <div data-testid="pipeline-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pipeline</h1>
          <p className="page-subtitle">{applications.length} candidates across all stages</p>
        </div>
      </div>

      {/* Kanban Board — vertical swimlanes (one lane per stage) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {STAGES.map(stage => (
          <div key={stage} data-testid={`pipeline-column-${stage}`}
            style={{ display: 'flex', alignItems: 'stretch', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, overflow: 'hidden' }}>
            {/* Lane header (left side) */}
            <div style={{ width: 168, flexShrink: 0, background: '#FAF7F2', borderRight: `3px solid ${STAGE_COLORS[stage]}`, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.4rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: STAGE_COLORS[stage] }} />
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3' }}>{STAGE_LABELS[stage]}</span>
              </div>
              <span style={{ fontSize: '0.6875rem', color: '#64748B' }}>
                {grouped[stage].length} candidate{grouped[stage].length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Lane body (cards flow horizontally within the lane) */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: '0.6rem', overflowX: 'auto', padding: '0.75rem 0.9rem', alignItems: 'stretch' }}>
              {grouped[stage].length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', color: '#CBD5E1', fontSize: '0.75rem', padding: '0.5rem 0.25rem' }}>No candidates</div>
              ) : (
                grouped[stage].map((app, i) => {
                  const tis = timeInStage(app);
                  return (
                    <Card key={`${stage}-${app.candidate_email}-${i}`}
                      data-testid="pipeline-candidate-card"
                      onClick={() => app.candidate_id && navigate(`/candidates/${app.candidate_id}`)}
                      style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 10, padding: '0.75rem', cursor: app.candidate_id ? 'pointer' : 'default', width: 236, flexShrink: 0 }}
                      className="candidate-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${STAGE_COLORS[stage]}, ${STAGE_COLORS[stage]}80)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                          {app.candidate_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#0076A3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.candidate_name}</div>
                          <div style={{ fontSize: '0.5625rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.job_title}</div>
                        </div>
                        {app.score != null && (
                          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.6875rem', fontWeight: 700, color: app.score >= 80 ? '#059669' : app.score >= 60 ? '#0076A3' : '#D97706' }}>{app.score}</span>
                        )}
                      </div>

                      {/* Compact journey bar */}
                      {app.journey && <CandidateJourneyCompact journey={app.journey} />}

                      {/* Time spent in current stage */}
                      <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.625rem', color: '#94A3B8' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          {tis != null ? `${fmtDays(tis)} in ${STAGE_LABELS[stage]}` : `In ${STAGE_LABELS[stage]}`}
                        </span>
                        {app.fit && <span>{app.fit}</span>}
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
